import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    // Sablony udalosti
    SABLONY_UDALOSTI_URL,
    SABLONY_UDALOSTI_ALL_URL,
    SABLONY_UDALOSTI_BY_ID_URL,
    SABLONY_UDALOSTI_VYHLASIT_URL,
    SABLONY_UDALOSTI_TOGGLE_STATUS_URL,
    // Users
    ADMIN_USERS_URL,
    ADMIN_USER_BY_ID_URL,
    ADMIN_USER_RESET_PASSWORD_URL,
    ADMIN_USER_TOGGLE_STATUS_URL,
    ADMIN_USER_ROLE_URL,
    ADMIN_USERS_ADMINS_URL,
    ADMIN_USERS_SEARCH_URL,
    ADMIN_USER_PIN_URL,
    ADMIN_USER_TEST_NOTIFICATION_URL,
    ADMIN_USER_RESET_TEST_LIMIT_URL,
    ADMIN_USER_TEST_HISTORY_URL,
    ADMIN_USER_BULK_SET_PIN_URL,
    ADMIN_USER_BULK_DELETE_PIN_URL,
    ADMIN_USER_SEND_INVITATION_URL,
    // Health
    ADMIN_HEALTH_URL,
    type HealthComponent,
    // Audit
    ADMIN_AUDIT_URL,
    ADMIN_AUDIT_RECENT_URL,
    ADMIN_AUDIT_STATISTICS_URL,
    ADMIN_AUDIT_EXPORT_CSV_URL,
    ADMIN_AUDIT_CLEANUP_URL,
    // Konfigurace
    ADMIN_KONFIGURACE_UPDATE_URL,
    ADMIN_KONFIGURACE_TRIGGER_REFRESH_URL,
    // Události
    UDALOSTI_ALL_URL,
    // Typy událostí
    TYPY_UDALOSTI_URL,
    TYPY_UDALOSTI_PODTYPY_URL,
    TYPY_UDALOSTI_ALL_PODTYPY_URL,
} from "../url";
import type {
    SablonaUdalosti,
    CreateSablonaRequest,
    UpdateSablonaRequest,
    VyhlasitZeSablonyRequest,
    Uzivatel,
    CreateUserRequest,
    UpdateUserRequest,
    SystemHealthResponse,
    ComponentHealth,
    AuditLog,
    PagedResponseAuditLog,
    AuditStatistics,
    CreateUdalostRequest,
    CreateUdalostResponse,
    TypUdalosti,
    PodtypUdalosti,
    UserTestNotificationRequest,
    UserTestNotificationResponse,
    ResetTestLimitResponse,
    ScheduledTestPoplachResponse,
    BulkSetPinRequest,
    BulkDeletePinRequest,
} from "../generated";

// -----------------------------
// USERS
// -----------------------------

const getAllUsers = () => {
    return handleRequest<Uzivatel[]>(() =>
        axiosInstance.get(ADMIN_USERS_URL)
    );
};

export interface CreateUserResult {
    user: Uzivatel;
    generatedPin: string | null;
}

const createUser = (data: CreateUserRequest) => {
    return handleRequest<CreateUserResult>(() =>
        axiosInstance.post(ADMIN_USERS_URL, data)
    );
};

const getUserById = (id: number) => {
    return handleRequest<Uzivatel>(() =>
        axiosInstance.get(ADMIN_USER_BY_ID_URL(id))
    );
};

const updateUser = (id: number, data: UpdateUserRequest) => {
    return handleRequest<Uzivatel>(() =>
        axiosInstance.put(ADMIN_USER_BY_ID_URL(id), data)
    );
};

const deleteUser = (id: number) => {
    return handleRequest(() =>
        axiosInstance.delete(ADMIN_USER_BY_ID_URL(id))
    );
};

const resetUserPassword = (id: number, newPassword?: string, sendPasswordEmail?: boolean) => {
    return handleRequest(() =>
        axiosInstance.post(ADMIN_USER_RESET_PASSWORD_URL(id), { newPassword, sendPasswordEmail })
    );
};

const toggleUserStatus = (id: number, aktivni: boolean) => {
    return handleRequest<Uzivatel>(() =>
        axiosInstance.patch(ADMIN_USER_TOGGLE_STATUS_URL(id), null, { params: { aktivni } })
    );
};

const changeUserRole = (id: number, role: string) => {
    return handleRequest<Uzivatel>(() =>
        axiosInstance.post(ADMIN_USER_ROLE_URL(id), null, { params: { role } })
    );
};

const getAdmins = () => {
    return handleRequest<Uzivatel[]>(() =>
        axiosInstance.get(ADMIN_USERS_ADMINS_URL)
    );
};

const searchUsers = (query?: string) => {
    return handleRequest<Uzivatel[]>(() =>
        axiosInstance.get(ADMIN_USERS_SEARCH_URL, { params: { query } })
    );
};

const sendUserTestNotification = (id: number, data?: UserTestNotificationRequest) => {
    return handleRequest<UserTestNotificationResponse>(() =>
        axiosInstance.post(ADMIN_USER_TEST_NOTIFICATION_URL(id), data || { fcm: true })
    );
};

const resetUserTestLimit = (id: number) => {
    return handleRequest<ResetTestLimitResponse>(() =>
        axiosInstance.post(ADMIN_USER_RESET_TEST_LIMIT_URL(id))
    );
};

const getUserTestHistory = (id: number) => {
    return handleRequest<ScheduledTestPoplachResponse[]>(() =>
        axiosInstance.get(ADMIN_USER_TEST_HISTORY_URL(id))
    );
};

const sendInvitation = (id: number) => {
    return handleRequest<{ message: string; expiresAt: string }>(() =>
        axiosInstance.post(ADMIN_USER_SEND_INVITATION_URL(id))
    );
};

// -----------------------------
// PIN MANAGEMENT
// -----------------------------

const getUserPin = (id: number) => {
    return handleRequest<{ pin: string; hasPin: boolean }>(() =>
        axiosInstance.get(ADMIN_USER_PIN_URL(id))
    );
};

const setUserPin = (id: number, pin: string) => {
    return handleRequest(() =>
        axiosInstance.post(ADMIN_USER_PIN_URL(id), { pin })
    );
};

const deleteUserPin = (id: number) => {
    return handleRequest(() =>
        axiosInstance.delete(ADMIN_USER_PIN_URL(id))
    );
};

const bulkSetPin = (data: BulkSetPinRequest) => {
    return handleRequest<{ message: string }>(() =>
        axiosInstance.post(ADMIN_USER_BULK_SET_PIN_URL, data)
    );
};

const bulkDeletePin = (data: BulkDeletePinRequest) => {
    return handleRequest<{ message: string }>(() =>
        axiosInstance.post(ADMIN_USER_BULK_DELETE_PIN_URL, data)
    );
};

// -----------------------------
// HEALTH
// -----------------------------

const getHealth = (component?: HealthComponent) => {
    return handleRequest<SystemHealthResponse | ComponentHealth>(() =>
        axiosInstance.get(ADMIN_HEALTH_URL(component))
    );
};

const getSystemHealth = () => getHealth('all') as Promise<SystemHealthResponse>;
const getDatabaseHealth = () => getHealth('database') as Promise<ComponentHealth>;
const getEmailHealth = () => getHealth('email') as Promise<ComponentHealth>;
const getFcmHealth = () => getHealth('fcm') as Promise<ComponentHealth>;
const getTtsHealth = () => getHealth('tts') as Promise<ComponentHealth>;
const getMqttGatewayHealth = () => getHealth('mqtt-gateway') as Promise<ComponentHealth>;
const getDevicesHealth = () => getHealth('devices') as Promise<ComponentHealth>;

// -----------------------------
// AUDIT
// -----------------------------

interface AuditSearchParams {
    userId?: number;
    action?: string;
    entityType?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    size?: number;
}

const searchAuditLogs = (params?: AuditSearchParams) => {
    return handleRequest<PagedResponseAuditLog>(() =>
        axiosInstance.get(ADMIN_AUDIT_URL, { params })
    );
};

const getRecentAuditLogs = (limit = 50) => {
    return handleRequest<AuditLog[]>(() =>
        axiosInstance.get(ADMIN_AUDIT_RECENT_URL, { params: { limit } })
    );
};

const getAuditStatistics = () => {
    return handleRequest<AuditStatistics>(() =>
        axiosInstance.get(ADMIN_AUDIT_STATISTICS_URL)
    );
};

const exportAuditCsv = async (params?: AuditSearchParams): Promise<Blob> => {
    const res = await axiosInstance.get(ADMIN_AUDIT_EXPORT_CSV_URL, {
        params,
        responseType: 'blob',
    });
    return res.data;
};

const cleanupAuditLogs = (olderThanDays: number) => {
    return handleRequest<{ deletedCount: number }>(() =>
        axiosInstance.delete(ADMIN_AUDIT_CLEANUP_URL, {
            params: { olderThanDays },
        })
    );
};

// -----------------------------
// KONFIGURACE
// -----------------------------

const updateAdminKonfigurace = (klic: string, hodnota: string) => {
    return handleRequest(() =>
        axiosInstance.put(ADMIN_KONFIGURACE_UPDATE_URL(klic), { hodnota })
    );
};

const triggerRefresh = () => {
    return handleRequest(() =>
        axiosInstance.post(ADMIN_KONFIGURACE_TRIGGER_REFRESH_URL)
    );
};

// -----------------------------
// UDÁLOSTI (POPLACHY)
// -----------------------------

const createUdalost = (data: CreateUdalostRequest) => {
    return handleRequest<CreateUdalostResponse>(() =>
        axiosInstance.post(UDALOSTI_ALL_URL, data)
    );
};

// -----------------------------
// TYPY UDÁLOSTÍ
// -----------------------------

const getTypyUdalosti = () => {
    return handleRequest<TypUdalosti[]>(() =>
        axiosInstance.get(TYPY_UDALOSTI_URL)
    );
};

const getPodtypyByTypId = (typId: number) => {
    return handleRequest<PodtypUdalosti[]>(() =>
        axiosInstance.get(TYPY_UDALOSTI_PODTYPY_URL(typId))
    );
};

const getAllPodtypy = () => {
    return handleRequest<PodtypUdalosti[]>(() =>
        axiosInstance.get(TYPY_UDALOSTI_ALL_PODTYPY_URL)
    );
};

// -----------------------------
// ŠABLONY UDÁLOSTÍ
// -----------------------------

const getSablonyUdalosti = () => {
    return handleRequest<SablonaUdalosti[]>(() =>
        axiosInstance.get(SABLONY_UDALOSTI_URL)
    );
};

const getAllSablonyUdalosti = () => {
    return handleRequest<SablonaUdalosti[]>(() =>
        axiosInstance.get(SABLONY_UDALOSTI_ALL_URL)
    );
};

const getSablonaById = (id: number) => {
    return handleRequest<SablonaUdalosti>(() =>
        axiosInstance.get(SABLONY_UDALOSTI_BY_ID_URL(id))
    );
};

const createSablona = (data: CreateSablonaRequest) => {
    return handleRequest<SablonaUdalosti>(() =>
        axiosInstance.post(SABLONY_UDALOSTI_URL, data)
    );
};

const updateSablona = (id: number, data: UpdateSablonaRequest) => {
    return handleRequest<SablonaUdalosti>(() =>
        axiosInstance.put(SABLONY_UDALOSTI_BY_ID_URL(id), data)
    );
};

const deleteSablona = (id: number) => {
    return handleRequest<{ message: string }>(() =>
        axiosInstance.delete(SABLONY_UDALOSTI_BY_ID_URL(id))
    );
};

const toggleSablonaStatus = (id: number, aktivni: boolean) => {
    return handleRequest<{ message: string }>(() =>
        axiosInstance.patch(SABLONY_UDALOSTI_TOGGLE_STATUS_URL(id), null, { params: { aktivni } })
    );
};

const vyhlasitZeSablony = (id: number, data?: VyhlasitZeSablonyRequest) => {
    return handleRequest<CreateUdalostResponse>(() =>
        axiosInstance.post(SABLONY_UDALOSTI_VYHLASIT_URL(id), data || {})
    );
};

// -----------------------------

const AdminService = {
    // Users
    getAllUsers,
    createUser,
    getUserById,
    updateUser,
    deleteUser,
    resetUserPassword,
    toggleUserStatus,
    changeUserRole,
    getAdmins,
    searchUsers,
    sendUserTestNotification,
    resetUserTestLimit,
    getUserTestHistory,
    sendInvitation,
    // PIN management
    getUserPin,
    setUserPin,
    deleteUserPin,
    bulkSetPin,
    bulkDeletePin,
    // Health
    getHealth,
    getSystemHealth,
    getDatabaseHealth,
    getEmailHealth,
    getFcmHealth,
    getTtsHealth,
    getMqttGatewayHealth,
    getDevicesHealth,
    // Audit
    searchAuditLogs,
    getRecentAuditLogs,
    getAuditStatistics,
    exportAuditCsv,
    cleanupAuditLogs,
    // Konfigurace
    updateAdminKonfigurace,
    triggerRefresh,
    // Události
    createUdalost,
    // Typy událostí
    getTypyUdalosti,
    getPodtypyByTypId,
    getAllPodtypy,
    // Šablony událostí
    getSablonyUdalosti,
    getAllSablonyUdalosti,
    getSablonaById,
    createSablona,
    updateSablona,
    deleteSablona,
    toggleSablonaStatus,
    vyhlasitZeSablony,
};

export default AdminService;
